import axios, { AxiosError, AxiosRequestConfig } from "axios";
import type { ApiError } from "../types";

// Base API URL from environment or default to relative path
const API_URL = import.meta.env.VITE_API_URL || "/api";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Token will be added by the useApi hook when available
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const apiError: ApiError = {
      detail: error.response?.data?.detail || error.message || "Unknown error",
      status_code: error.response?.status,
    };
    return Promise.reject(apiError);
  }
);

// Helper function to make authenticated requests
export async function authenticatedRequest<T>(
  config: AxiosRequestConfig,
  token?: string
): Promise<T> {
  const headers = token
    ? { ...config.headers, Authorization: `Bearer ${token}` }
    : config.headers;

  const response = await apiClient.request<T>({
    ...config,
    headers,
  });

  return response.data;
}

export default apiClient;

